import { isNil, isNumber } from 'lodash-es';
import { Nullable } from 'app-types';
import { getPriceWithCurrency } from './tablesUtils';
import { getTextValue } from './utils';
import { toNegative, roundNumber } from './converters';
import { PricingInvoicesResponse } from 'AppGS/api/finance/financeTypes';
import {
	calculateWaitingTimeFee,
	isDiscountAppliedToWaitingTime,
	isDiscountKey,
} from 'components/ReviewPricingModal/ModalPricingHelpers';
import { ReviewPricingData } from 'components/ReviewPricingModal/ReviewPricingModalTypes';

export const calculateTotalSummary = (type: 'net' | 'vat' | 'total') => (
	record: PricingInvoicesResponse
) => {
	const { servicePartnerToCustomerCurrencyRate = 1 } = record;
	const baseFeePropName = isNil(record.servicePartnerBaseFeeOverwritten)
		? 'servicePartnerBaseFee'
		: 'servicePartnerBaseFeeOverwritten';
	const keys: Array<keyof PricingInvoicesResponse> = [
		'servicePartnerCarParkFee',
		'servicePartnerExtrasFee',
	];
	const waitingTimeFee = calculateWaitingTimeFee(record);
	const spNet = keys.reduce((prev, key) => {
		const recordValue = record[key];
		const value: number = isNumber(recordValue) ? recordValue : 0;
		return prev + value;
	}, waitingTimeFee);

	const percentKeys = ['Discount', 'Gratuity', 'FuelSurcharge', 'Admin'];
	const percentNet = percentKeys.reduce((prev, key) => {
		const value = calculateValue(key, record) || 0;
		return prev + value;
	}, 0);

	const bookingFee = getBookingFee(record);
	const spBase = record[baseFeePropName];
	const spNetTotal =
		(spBase + percentNet + spNet + bookingFee) * servicePartnerToCustomerCurrencyRate;
	const totalNet = spNetTotal + getGsNet(record);

	const {
		internalServicePartnerCarTypeVatRatePercentage,
		groundScopeVatRatePercentage,
		integratedServicePartnerVatFee,
	} = record;
	const spVat: number = !isNil(integratedServicePartnerVatFee)
		? integratedServicePartnerVatFee
		: calculatePercentFee(spNetTotal, internalServicePartnerCarTypeVatRatePercentage);
	const gsVat: number = calculatePercentFee(getGsNet(record), groundScopeVatRatePercentage);
	const totalVat = spVat + gsVat;
	const totalFee = Number(roundNumber(totalVat)) + Number(roundNumber(totalNet));
	switch (type) {
		case 'net':
			return totalNet;
		case 'vat':
			return totalVat;
		case 'total':
			return totalFee;
		default:
			return 0;
	}
};

export function calculatePercentFee(
	value: Nullable<number>,
	vatPercent: Nullable<number>,
	defaultValue: any = 0
): typeof defaultValue | number {
	if (isNil(value) || isNil(vatPercent)) {
		return defaultValue;
	}
	return value * (vatPercent * 0.01);
}

export function calculateValue(keyName: string, record: ReviewPricingData) {
	let base = getBaseValue(
		record.servicePartnerBaseFee,
		record['servicePartnerBaseFeeOverwritten']
	);
	const overwrittenPercent = record[`servicePartner${keyName}PercentagesOverwritten`];
	const appliedPercent = record[`appliedServicePartner${keyName}Percentages`];
	if (overwrittenPercent === null && appliedPercent === null) {
		return null;
	}
	if (isDiscountAppliedToWaitingTime(keyName, record)) {
		base = base + calculateWaitingTimeFee(record);
	}
	const percentFee = getBaseValue(appliedPercent, overwrittenPercent);
	const baseFee = calculatePercentFee(base, percentFee);
	if (isDiscountKey(keyName)) {
		return toNegative(baseFee);
	}
	return baseFee;
}

export const getCalculateValue = (keyName: string) => {
	return (_: unknown, record: any) => {
		const { servicePartnerToCustomerCurrencyRate = 1 } = record;
		return getPriceWithCurrency(
			calculateValue(keyName, record) * servicePartnerToCustomerCurrencyRate,
			record.customerCurrencySymbol
		);
	};
};

export const getGsNet = (record: any) => {
	const { groundScopeBaseFeeOverwritten, groundScopeBaseFee } = record;
	return !isNil(record.groundScopeBaseFeeOverwritten)
		? groundScopeBaseFeeOverwritten
		: groundScopeBaseFee;
};

export const getGsVat = (record: any) => {
	const { groundScopeVatRatePercentage } = record;
	return !isNil(groundScopeVatRatePercentage)
		? calculatePercentFee(getGsNet(record), groundScopeVatRatePercentage)
		: undefined;
};

export const getGsTotal = (record: any) => {
	const vat = getGsVat(record) || 0;
	return vat + getGsNet(record);
};

export function getBookingFee(record: any) {
	const { servicePartnerBookingFeeOverwritten, appliedServicePartnerBookingFee } = record;
	return getBaseValue(appliedServicePartnerBookingFee, servicePartnerBookingFeeOverwritten);
}

export const getBaseValue = (value: number, valueOverwritten?: number) => {
	return isNil(valueOverwritten) ? value : valueOverwritten;
};

export const getFinancePlaceholder = (placeholder?: string, currency?: string) => {
	return `${placeholder || currency || '%'} —`;
};

export const getFinanceInputSign = (placeholder?: string, currency?: string) => {
	return `${placeholder || currency || '%'}`;
};

export const showValueInPercent = (value: number | string) => {
	return `${Number(value).toFixed(1)}%`;
};

export function getPriceWithCurrencyText(value: Nullable<number | string>, currencySymbol: string) {
	return `${currencySymbol} ${isNil(value) ? getTextValue(value) : roundNumber(value)}`;
}
